import React, { useEffect, useRef, useState } from 'react';
import Icon from './icons';
import { BrandPicker } from '../brand';
import ItemList, { DragSrc } from './ItemList';
import TemplateBar from './TemplateBar';
import SendModal from './SendModal';
import { getPerson, getCard, cardType, rid, coItems, repOptions, setRep, portalItems } from './collect';
import { createCollectPortal, syncCollectPortal } from './api';
import type { CollectItemPayload } from './api';
import { officeOf, portalOrigin } from './types';
import type { Collect, PersonCard, Project, SendLog, Office } from './types';
import { regCoOf } from './coterms';

interface Props {
  refId: string;
  card: PersonCard;
  collect: Collect;
  p: Project;
  edit: (fn: (c: Collect) => void) => void;
  dragSrc: DragSrc | null;
  setDragSrc: (d: DragSrc | null) => void;
  toast: (m: string) => void;
  onAfterSend: (ref: string) => void;
  onOpenPortal: (ref: string) => void;
  onOpenSwap: (ref: string, coId: string) => void;
  onDeleteLp: (ref: string) => void;
  onOffice?: (o: Office) => void;   // 「로고」 토글 → 프로젝트 진행 주체 변경 (등록정보·목록 배지·다른 카드·기존 포털까지)
}

export default function CollectList({ refId, card, collect, p, edit, dragSrc, setDragSrc, toast, onAfterSend, onOpenPortal, onOpenSwap, onDeleteLp, onOffice }: Props) {
  const type = cardType(refId);
  const mineCos = collect.cos.filter((co) => co.rep === refId);
  const [sendOpen, setSendOpen] = useState(false);
  const [portalUrl, setPortalUrl] = useState('');
  const [activePid, setActivePid] = useState('');
  const [busy, setBusy] = useState(false);

  const portalLink = (pid: string) => portalOrigin() + '/api/portal/' + pid + '/share';

  const buildItemsPayload = (c: Collect): CollectItemPayload[] => portalItems(c, refId);   // LP 후보 링크와 같은 모양 (collect.ts)

  // 항목(제목·유형·설명·가이드)·브랜드 변경 시 포털에 실시간 반영 (디바운스 1.2s, 포털 있을 때만)
  const brand = officeOf(p);   // 고객 화면 로고 = 프로젝트 진행 주체 (카드마다 따로가 아니라 한 프로젝트에 하나, 2026-09-24)
  const syncSnap = JSON.stringify({ items: buildItemsPayload(collect), name: card.name || '', brand });
  const lastSyncRef = useRef<string>('');
  useEffect(() => {
    const cpid = card.collectProjectId;
    if (!cpid || lastSyncRef.current === '' || lastSyncRef.current === syncSnap) { lastSyncRef.current = syncSnap; return; }
    const snap = syncSnap;
    const t = window.setTimeout(() => {
      const companyId = (mineCos.find((co) => co.companyId) || {}).companyId || null;
      syncCollectPortal(p.id, cpid, buildItemsPayload(collect), { name: card.name || '', ceo: card.name || '', brand, companyId })
        .then((r) => { lastSyncRef.current = snap; if (r.kept) toast('제출물이 있는 항목 ' + r.kept + '건은 포털에 남겨 두었습니다'); })
        .catch((e) => toast('포털 반영 실패: ' + ((e as Error).message || '')));
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncSnap, card.collectProjectId, p.id]);

  // 포털(자료수집 프로젝트)이 없으면 생성, 있으면 재사용
  const ensurePortal = async (): Promise<string> => {
    if (card.collectProjectId) {
      try { await syncCollectPortal(p.id, card.collectProjectId, buildItemsPayload(collect), { name: card.name || '', ceo: card.name || '', brand, companyId: (mineCos.find((co) => co.companyId) || {}).companyId || null }); } catch (e) { /* best effort, 자동 동기화가 보완 */ }
      return card.collectProjectId;
    }
    const recip = (card.recips && card.recips[0]) || { tel: '', email: '' };
    const res = await createCollectPortal(p.id, {
      name: card.name || (type === 'gp' ? 'GP' : 'LP'),
      biz_no: card.bizno || '',
      ceo: card.name || '',
      ceo_phone: recip.tel || '',
      ceo_email: recip.email || '',
      label: (p.org || '벤처') + ' 자료수집',
      items: buildItemsPayload(collect),
      brand,
      // 이 사람이 대표하는 법인의 업무관리 업체 ID — 없으면 결과 화면의 「TM 저장소 저장」이 숨겨진다
      companyId: (mineCos.find((co) => co.companyId) || {}).companyId || null,
    });
    const pid = res.project_id;
    edit((c) => { const cc = getPerson(c, refId); cc.collectProjectId = pid; cc.url = pid; });
    return pid;
  };

  const copyUrl = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const pid = await ensurePortal();
      const url = portalLink(pid);
      if (navigator.clipboard) await navigator.clipboard.writeText(url).then(() => toast('제출 링크 복사됨'), () => toast(url));
      else toast(url);
    } catch (e) { toast('링크 생성 실패: ' + ((e as Error).message || '')); } finally { setBusy(false); }
  };

  const openSend = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const pid = await ensurePortal();
      setActivePid(pid);
      setPortalUrl(portalLink(pid));
      setSendOpen(true);
    } catch (e) { toast('포털 생성 실패: ' + ((e as Error).message || '')); } finally { setBusy(false); }
  };

  const handleSend = (log: SendLog) => {
    edit((c) => {
      const cc = getPerson(c, refId);
      cc.sent = true;
      cc.sendLog = [...(cc.sendLog || []), log];
    });
    setSendOpen(false);
    onAfterSend(refId);
    const okN = log.recips.filter((r) => r.status === '성공').length;
    const failN = log.recips.length - okN;
    toast('자료요청 전송 · 성공 ' + okN + '건' + (failN ? ' · 실패 ' + failN + '건' : ''));
  };

  return (
    <>
      <div className="cph"><b>수집 항목</b>
        <div className="cpacts">
          {/* 고객 제출 화면 우측 상단 로고 — 기존(로움경영지도사무소) / 올댓벤처스. 바꾸면 포털에 즉시 반영, URL 은 그대로 (2026-09-11) */}
          <BrandPicker size="sm" label="로고" only={['roum', 'atv']} value={brand} onChange={(v) => { const o: Office = v === 'atv' ? 'atv' : 'roum'; if (onOffice) onOffice(o); else edit((c) => { getPerson(c, refId).brand = o; }); }} />
          <button className="cact" onClick={copyUrl}>🔗 URL 복사</button>
          <button className="cact" onClick={() => onOpenPortal(refId)}>포탈 미리보기</button>
        </div>
      </div>
      {type === 'lp' ? (
        <input className="bizno" value={card.name} onChange={(e) => edit((c) => { getPerson(c, refId).name = e.target.value; })} placeholder="LP 성명" />
      ) : null}
      <TemplateBar refId={refId} card={card} edit={edit} toast={toast} />
      <div className="lbl">{type === 'gp' ? 'GP' : 'LP'} 개인 자료</div>
      <ItemList refId={refId} items={card.items} scope="person" sent={!!card.collectProjectId} edit={edit} dragSrc={dragSrc} setDragSrc={setDragSrc} />

      {mineCos.map((co) => (
        <React.Fragment key={co.id}>
          <div className="mergesec" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ flex: 1, minWidth: 0 }}>🏢 대표 투자법인 — {co.name || co.bizno || '(법인명 미입력)'}</span>
            <button className="cact" style={{ whiteSpace: 'nowrap' }} onClick={() => onOpenSwap(refId, co.id)}>🔁 교체</button>
            <button className="cact" style={{ whiteSpace: 'nowrap', color: 'var(--muted-2)' }} onClick={() => edit((c) => { c.cos = c.cos.filter((x) => x.id !== co.id); })}>삭제</button>
          </div>
          <input className="bizno" value={co.name} onChange={(e) => edit((c) => { const x = getCard(c, 'co:' + co.id); x.name = e.target.value; })} placeholder="투자법인 명칭" />
          <input className="bizno" value={co.bizno || ''} onChange={(e) => edit((c) => { const x = getCard(c, 'co:' + co.id); x.bizno = e.target.value; })} placeholder="법인 사업자등록번호" />
          <div className="repsel sm"><label>이 법인의 대표(소속)</label>
            <select value={co.rep} onChange={(e) => edit((c) => setRep(c, co.id, e.target.value))}>
              {repOptions(collect).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <TemplateBar refId={'co:' + co.id} card={co} edit={edit} toast={toast} trackDefault={false} label={'법인 서류 템플릿 · 단계연동 포함'} />
          <ItemList refId={'co:' + co.id} items={co.items} scope="co" sent={!!card.collectProjectId} edit={edit} dragSrc={dragSrc} setDragSrc={setDragSrc} />
        </React.Fragment>
      ))}
      <div style={{ textAlign: 'right', margin: '6px 0 2px' }}>
        <button className="cact" onClick={() => edit((c) => { c.cos.push({ id: rid(), name: '', bizno: '', rep: refId, items: coItems() }); })}>
          ＋ {type === 'gp' ? 'GP' : '이 LP'} 대표 법인 추가
        </button>
      </div>

      <div className="lbl">받는사람 (연락처 둘 다 있으면 문자·이메일 동시 발송)</div>
      {card.recips.map((r, i) => (
        <div className="reciprow" key={i}>
          <input className="rcname" value={r.name} onChange={(e) => edit((c) => { getPerson(c, refId).recips[i].name = e.target.value; })} placeholder={r.role || '이름/역할'} />
          <input value={r.tel} onChange={(e) => edit((c) => { getPerson(c, refId).recips[i].tel = e.target.value; })} placeholder="휴대폰" />
          <input value={r.email} onChange={(e) => edit((c) => { getPerson(c, refId).recips[i].email = e.target.value; })} placeholder="이메일" />
          {card.recips.length > 1 ? <button className="rxbtn" onClick={() => edit((c) => { const rc = getPerson(c, refId).recips; if (rc.length > 1) rc.splice(i, 1); })}><Icon n="x" /></button> : null}
        </div>
      ))}
      <div className="recipadd">
        <button className="gbtn" onClick={() => edit((c) => { getPerson(c, refId).recips.push({ name: '', role: '', tel: '', email: '' }); })}>+ 받는사람 추가</button>
        <button className="gbtn" onClick={() => edit((c) => {
          // 소개자는 투자법인 단위 — 이 사람이 대표하는 법인들의 FP를 중복 없이 추가 (없으면 첫 법인 FP)
          const rc = getPerson(c, refId).recips;
          const src = (mineCos.length ? mineCos : collect.cos.slice(0, 1)).map((co) => regCoOf(p, co)).filter((x) => !!x && !!x.fpName);
          const seen = new Set(rc.map((r) => r.name + '|' + r.tel));
          let added = 0;
          src.forEach((x) => { if (!x) return; const k = x.fpName + '|' + (x.fpTel || ''); if (seen.has(k)) return; seen.add(k); rc.push({ name: x.fpName, role: '소개 FP', tel: x.fpTel || '', email: '' }); added++; });
          if (!added) rc.push({ name: '', role: '소개 FP', tel: '', email: '' });
        })}>+ FP 추가</button>
      </div>
      <input className="bizno" value={card.bizno || ''} onChange={(e) => edit((c) => { getPerson(c, refId).bizno = e.target.value; })} placeholder={'사업자등록번호 · 업무관리 업체 등록용 ' + (type !== 'co' ? '(법인 대표면 자동)' : '')} />

      <button className="sendbtn" onClick={openSend} disabled={busy}><Icon n="send" style={{ width: 14, height: 14 }} /> {busy ? '포털 준비 중…' : (card.sent ? '재전송 (자료요청)' : '자료요청 전송')}</button>

      {card.sendLog && card.sendLog.length ? (
        <>
          <div className="lbl" style={{ marginTop: 10 }}>전송 이력</div>
          {[...card.sendLog].reverse().map((lg, i) => {
            const okN = lg.recips.filter((x) => x.status === '성공').length;
            const failN = lg.recips.length - okN;
            return (
              <div className="s3file" key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span className="rf" style={{ flex: 1, minWidth: 0 }}>📨 {lg.at} · {lg.channel} · {lg.recips.map((x) => x.name).join(', ')}</span>
                <span className="pill p-green">성공 {okN}</span>
                {failN ? (
                  <span className="pill p-amber" title={lg.recips.filter((x) => x.status === '실패').map((x) => x.name + ': ' + (x.reason || '실패')).join(' / ')}>
                    실패 {failN}
                  </span>
                ) : null}
              </div>
            );
          })}
        </>
      ) : null}

      {type === 'lp' ? (
        <button className="delrow" onClick={() => onDeleteLp(refId)}>이 LP 삭제</button>
      ) : null}

      {sendOpen ? <SendModal p={p} card={card} portalUrl={portalUrl} collectProjectId={activePid || card.collectProjectId || ''} onClose={() => setSendOpen(false)} onSend={handleSend} /> : null}
    </>
  );
}
